import React, { useMemo } from 'react';
import {
    Table,
    TableRow,
    TableBody,
    Box,
    useTheme,
    TableContainer,
    Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
    IBasicUserFragment,
    IContentTypeValue,
    IPortfolioItemReportFragment,
} from 'graphql/types';
import { TableCell, TableHead } from 'common/components/Table';
import { Typography } from 'common/components/Typography';
import { BoxLoader } from 'common/components/Loader';
import { Pagination } from 'common/components/Pagination';
import { InlineStickyWrapper } from 'common/components/InlineStickyWrapper';
import { useReportList } from 'report/hooks';
import { UserAvatar } from 'user/components/UserAvatar';
import { FilterBar } from 'common/components/FilterBar';
import { SkillReportMatrixCell } from 'skill/components/SkillReportMatrix/SkillReportMatrixCell';
import { useSkillUserList } from 'skill/hooks/useSkillUserList';
import { IFilterFormItem, IGlobalDrawerType, TInputOption } from 'common/types';
import { useSkillReportList } from 'skill/hooks/useSkillReportList';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';

export const SkillReportMatrix = () => {
    const [translate] = useTranslation();
    const { openGlobalDrawer, closeGlobalDrawer } = useGlobalDrawer();
    const location = useLocation();

    const {
        skills,
        filterValues: skillFilterValues,
        loading: loadingSkills,
        filters: skillFilters,
    } = useSkillReportList();

    const theme = useTheme();
    // Calculate the total height of the navigation bar + context action bar
    const navHeights = (theme.mixins.toolbar.height as number) || 0;

    const {
        users,
        usersCount,
        loading: loadingUsers,
        paginationSettings,
        filters: userFilters,
        filterValues,
        onSearch,
        onFilterChange: onUserFilterChange,
        setPage,
    } = useSkillUserList();

    // Skill filter for report
    const skillFilterIds = skillFilterValues?.skillIds?.map(
        (skillInput: TInputOption) => skillInput.id
    );
    const relatedObjectUids = !!skillFilterIds?.length
        ? { skillIds: skillFilterIds }
        : undefined;

    const {
        items: skillReportItems,
        loading: loadingReports,
        refetch,
    } = useReportList('skill', !usersCount, {
        relatedObjectTypes: [IContentTypeValue.Skill],
        userIds: users.map((user) => user.id),
        first: 1000,
        relatedObjectUids,
    });

    const handleSearch = (searchValue: string) => {
        setPage(1);
        onSearch?.(searchValue);
    };

    const loading = loadingReports || loadingSkills || loadingUsers;

    const stickyOffsetLeft = {
        xs: 'auto',
        sm: theme.spacing(3),
    };

    const stickyOffset = {
        backgroundColor: 'common.white',
        position: {
            xs: 'static',
            sm: 'sticky',
        },
        left: stickyOffsetLeft,
    };

    const skillReportRows = useMemo(() => {
        const rows = new Map<
            string,
            {
                user: IBasicUserFragment;
                reports: IPortfolioItemReportFragment[];
            }
        >();

        users.forEach((user) => {
            rows.set(user.id, {
                user: user,
                reports: [
                    ...(rows.get(user.id)?.reports || []),
                    ...skillReportItems.filter(
                        (item) => item.user?.id === user.id
                    ),
                ],
            });
        });

        return rows;
    }, [skillReportItems]);

    const filters = [...skillFilters, ...userFilters] as IFilterFormItem[];
    const skillReportRowList = Array.from(skillReportRows.values());
    const hasSkillReport = !!skills.length && !!skillReportRowList.length;

    return (
        <>
            <FilterBar
                useFilterDrawer
                filterDrawerProps={{
                    title: translate('reportPage.filterFormSkill.title'),
                    description: translate(
                        'reportPage.filterFormSkill.description'
                    ),
                    formProps: {
                        onReset: (values) => {
                            setPage(1);
                            onUserFilterChange(values);
                        },
                    },
                }}
                formFilters={filters}
                formFilterValues={filterValues}
                initialSearchValue={filterValues.q}
                placeholder={translate('filterBarPlaceholder')}
                onFiltersChange={(values) => {
                    setPage(1);
                    onUserFilterChange(values);
                }}
                onSearch={handleSearch}
                onSearchClear={() => handleSearch('')}
            />

            {!loading && !hasSkillReport && (
                <Box sx={{ mt: 8, textAlign: 'center' }}>
                    <Typography>{translate('noSkillsReports')}</Typography>
                </Box>
            )}

            {!loading && hasSkillReport && (
                <TableContainer sx={{ overflowX: 'initial', mt: 4 }}>
                    <Table stickyHeader>
                        {!!skills.length && (
                            <TableHead>
                                <TableRow sx={{ verticalAlign: 'bottom' }}>
                                    <TableCell
                                        colSpan={0}
                                        sx={{
                                            top: navHeights,
                                            zIndex: 5,
                                            position: 'sticky',
                                            left: stickyOffsetLeft,
                                        }}
                                    >
                                        {translate('participant')}
                                    </TableCell>

                                    {skills.map((skill) => (
                                        <React.Fragment key={skill.id}>
                                            <TableCell
                                                vertical
                                                sx={[
                                                    {
                                                        top: navHeights,
                                                        zIndex: 4,
                                                        left: stickyOffsetLeft,
                                                    },
                                                ]}
                                            >
                                                <Tooltip title={skill.name}>
                                                    <Box>
                                                        {skill.shortName ||
                                                            skill.name.substring(
                                                                0,
                                                                12
                                                            )}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                        </React.Fragment>
                                    ))}
                                </TableRow>
                            </TableHead>
                        )}

                        {!loadingReports && (
                            <TableBody>
                                {skillReportRowList.map(({ user, reports }) => (
                                    <TableRow key={user.id}>
                                        <TableCell
                                            colSpan={0}
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => {
                                                openGlobalDrawer({
                                                    type: IGlobalDrawerType.UserProfile,
                                                    itemId: user.id,
                                                    props: {
                                                        onUpdate: () => {
                                                            refetch();
                                                        },
                                                        onArchive: () => {
                                                            refetch();

                                                            closeGlobalDrawer({
                                                                type: IGlobalDrawerType.UserProfile,
                                                            });
                                                        },
                                                    },
                                                });
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <UserAvatar
                                                    bgColor="secondary"
                                                    size={45}
                                                    user={user}
                                                />

                                                <Typography
                                                    sx={{ ml: 2 }}
                                                    variant="body2"
                                                >
                                                    {user.name}
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        {skills.map((skill) => {
                                            const report = reports.find(
                                                (report) =>
                                                    skill.id ===
                                                    report.relatedObjectId
                                            );

                                            return (
                                                <SkillReportMatrixCell
                                                    align="center"
                                                    key={skill.id}
                                                    report={report}
                                                    skill={skill}
                                                    sx={stickyOffset}
                                                    userId={user.id}
                                                    userName={user.name}
                                                    onUpdate={(
                                                        portfolioItemId
                                                    ) => {
                                                        refetch();

                                                        if (portfolioItemId) {
                                                            openGlobalDrawer(
                                                                {
                                                                    type: IGlobalDrawerType.PortfolioItem,
                                                                    itemId: portfolioItemId,
                                                                },
                                                                location
                                                            );
                                                        }
                                                    }}
                                                />
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        )}
                    </Table>
                </TableContainer>
            )}

            {loading && <BoxLoader />}

            {!loading && !!paginationSettings && hasSkillReport && (
                <Box sx={{ mt: 4 }}>
                    <InlineStickyWrapper fullWidth leftOffset={3}>
                        <Pagination
                            page={paginationSettings.page}
                            pageAmount={paginationSettings.pageAmount}
                            totalsAmount={paginationSettings.count}
                            totalsText={translate(
                                'reportPage.skillPagination.participants',
                                {
                                    count: paginationSettings.count,
                                }
                            )}
                            onChange={(page: number) => {
                                setPage?.(page);
                            }}
                        />
                    </InlineStickyWrapper>
                </Box>
            )}
        </>
    );
};
